import { FormEvent, useState, useEffect } from "react";
import axios from "../utils/axios";
import {
  formatChannelOptionLabel,
  sortChannelsByCategoryAndName,
} from "../utils/channelOptions";
import CollapsibleSection from "./CollapsibleSection";
import Spinner from "./Spinner";
import "./UserMonitoring.css";

interface UserMonitoringConfig {
  id: number;
  guild_id: string;
  user_id: string;
  channel_id: string;
  webhook_url: string;
  enabled: number;
  created_at: string;
  updated_at: string;
}

interface Channel {
  id: string;
  name: string;
  type?: number;
  parentId?: string | null;
  categoryName?: string | null;
}

interface UserMonitoringProps {
  guildId: string;
  channels: Channel[];
}

function UserMonitoring({ guildId, channels }: UserMonitoringProps) {
  const [configs, setConfigs] = useState<UserMonitoringConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Form state
  const [formUserId, setFormUserId] = useState("");
  const [formChannelId, setFormChannelId] = useState("");
  const [formWebhookUrl, setFormWebhookUrl] = useState("");
  const [formEnabled, setFormEnabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchConfigs();
  }, [guildId]);

  const fetchConfigs = async () => {
    try {
      const response = await axios.get(`/api/server/${guildId}/monitoring`);
      setConfigs(response.data.configs || []);
    } catch (error) {
      console.error("Error fetching monitoring configs:", error);
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormUserId("");
    setFormChannelId("");
    setFormWebhookUrl("");
    setFormEnabled(true);
    setEditingId(null);
    setShowAddForm(false);
  };

  const handleAdd = () => {
    resetForm();
    setShowAddForm(true);
  };

  const handleEdit = (config: UserMonitoringConfig) => {
    setFormUserId(config.user_id);
    setFormChannelId(config.channel_id);
    setFormWebhookUrl(config.webhook_url);
    setFormEnabled(config.enabled === 1);
    setEditingId(config.id);
    setShowAddForm(true);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    const trimmedUserId = formUserId.trim();
    const trimmedWebhookUrl = formWebhookUrl.trim();

    try {
      if (editingId) {
        // Update existing
        await axios.put(`/api/server/${guildId}/monitoring/${editingId}`, {
          userId: trimmedUserId,
          channelId: formChannelId,
          webhookUrl: trimmedWebhookUrl,
          enabled: formEnabled ? 1 : 0,
        });
      } else {
        // Create new
        await axios.post(`/api/server/${guildId}/monitoring`, {
          userId: trimmedUserId,
          channelId: formChannelId,
          webhookUrl: trimmedWebhookUrl,
        });
      }
      await fetchConfigs();
      resetForm();
    } catch (error: any) {
      console.error("Error saving monitoring config:", error);
      alert(error.response?.data?.error || "Failed to save monitoring config");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (
      !confirm("Are you sure you want to delete this monitoring configuration?")
    ) {
      return;
    }

    try {
      await axios.delete(`/api/server/${guildId}/monitoring/${id}`);
      await fetchConfigs();
    } catch (error) {
      console.error("Error deleting monitoring config:", error);
      alert("Failed to delete monitoring config");
    }
  };

  const handleToggleEnabled = async (config: UserMonitoringConfig) => {
    try {
      await axios.put(`/api/server/${guildId}/monitoring/${config.id}`, {
        userId: config.user_id,
        channelId: config.channel_id,
        webhookUrl: config.webhook_url,
        enabled: config.enabled === 1 ? 0 : 1,
      });
      await fetchConfigs();
    } catch (error) {
      console.error("Error toggling monitoring config:", error);
      alert("Failed to update monitoring config");
    }
  };

  const getChannelName = (channelId: string) => {
    const channel = channels?.find((c) => c.id === channelId);
    return channel ? formatChannelOptionLabel(channel) : channelId;
  };

  const textChannels = sortChannelsByCategoryAndName(
    (channels || []).filter((c) => c.type === 0),
  );

  if (loading) {
    return <Spinner size="medium" text="Loading monitoring configs..." />;
  }

  return (
    <CollapsibleSection
      title="👁️ Message Monitoring"
      count={configs?.length || 0}
      defaultCollapsed={true}
    >
      <div className="user-monitoring">
        <div className="monitoring-header">
          <div className="monitoring-description">
            <p>
              Monitor messages in specific channels and send them to webhooks
              (optionally filter by user and bots). By default, every user is
              monitored unless you specify a User/Bot ID.
            </p>
            <p>
              Webhooks are delivered as <strong>POST</strong> requests with
              <code>Content-Type: application/json</code>. Each payload contains
              the captured message metadata so your integration can decide what
              to do next. The body includes:
            </p>
            <ul>
              <li>
                <code>event</code>: Always <code>user_message</code> to identify
                the payload type.
              </li>
              <li>
                <code>user</code> / <code>user_id</code>: The message author's
                tag and ID.
              </li>
              <li>
                <code>guild_id</code>, <code>channel_id</code>,
                <code>message_id</code>: Location details for the message.
              </li>
              <li>
                <code>content</code>: The raw Discord message content.
              </li>
              <li>
                <code>is_bot</code>: <code>true</code> when the sender is a bot.
              </li>
            </ul>
            <p>
              The listener retries failed deliveries up to three times with
              exponential backoff, so transient webhook outages are handled
              automatically.
            </p>
          </div>
          <button className="btn btn-primary no-lift" onClick={handleAdd}>
            + Add
          </button>
        </div>

        {showAddForm && (
          <div className="monitoring-form-container">
            <form onSubmit={handleSubmit} className="monitoring-form">
              <h4>{editingId ? "Edit" : "Add"} Message Monitoring</h4>

              <div className="form-group">
                <label>User/Bot ID (Optional)</label>
                <input
                  type="text"
                  value={formUserId}
                  onChange={(e) => setFormUserId(e.target.value)}
                  placeholder="123456789012345678"
                  className="form-input"
                />
                <small>
                  Leave empty to monitor all users, or specify a user/bot ID to
                  monitor a specific user or bot
                </small>
              </div>

              <div className="form-group">
                <label>Channel *</label>
                <select
                  value={formChannelId}
                  onChange={(e) => setFormChannelId(e.target.value)}
                  required
                  className="form-select"
                >
                  <option value="">Select a channel</option>
                  {textChannels.map((channel) => (
                    <option key={channel.id} value={channel.id}>
                      {formatChannelOptionLabel(channel)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Webhook URL *</label>
                <input
                  type="url"
                  value={formWebhookUrl}
                  onChange={(e) => setFormWebhookUrl(e.target.value)}
                  placeholder="https://your-webhook-url.com/endpoint"
                  required
                  className="form-input"
                />
                <small>URL to send captured messages to</small>
              </div>

              {editingId && (
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formEnabled}
                      onChange={(e) => setFormEnabled(e.target.checked)}
                    />
                    <span>Enabled</span>
                  </label>
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={resetForm}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                >
                  {submitting ? "Saving..." : editingId ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        )}

        {!configs || configs.length === 0 ? (
          <div className="no-configs">
            <p>No message monitoring configured yet.</p>
            <p>Click "Add" to start monitoring messages in a channel.</p>
          </div>
        ) : (
          <div className="monitoring-list">
            {configs.map((config) => (
              <div
                key={config.id}
                className={`monitoring-item ${config.enabled === 0 ? "disabled" : ""}`}
              >
                <div className="monitoring-info">
                  <div className="monitoring-main">
                    {config.user_id && (
                      <span className="user-badge">
                        👤 User ID: {config.user_id}
                      </span>
                    )}
                    {!config.user_id && (
                      <span className="user-badge all-users">👥 All Users</span>
                    )}
                    <span className="channel-badge">
                      {getChannelName(config.channel_id)}
                    </span>
                    {config.enabled === 0 && (
                      <span className="status-badge disabled">Disabled</span>
                    )}
                    {config.enabled === 1 && (
                      <span className="status-badge enabled">Active</span>
                    )}
                  </div>
                  <div className="monitoring-webhook">
                    🔗 {config.webhook_url}
                  </div>
                  <div className="monitoring-meta">
                    Created: {new Date(config.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="monitoring-actions">
                  <button
                    className="btn btn-small btn-secondary"
                    onClick={() => handleToggleEnabled(config)}
                    title={config.enabled === 1 ? "Disable" : "Enable"}
                  >
                    {config.enabled === 1 ? "⏸️" : "▶️"}
                  </button>
                  <button
                    className="btn btn-small btn-secondary"
                    onClick={() => handleEdit(config)}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    className="btn btn-small btn-danger"
                    onClick={() => handleDelete(config.id)}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

export default UserMonitoring;
